import { useEffect, useState } from "react";
import api from "../../api/api";
import "./SkillModal.css";

// ─── helpers ────────────────────────────────────────────────
const isVideoUrl    = (url) => /(?:v=|youtu\.be\/)([^&?/]+)/.test(url);
const isPlaylistUrl = (url) => /[?&]list=/.test(url);

const extractVideoId = (url) => {
  const m = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
  return m ? m[1] : null;
};

const getThumbnail = (url) => {
  if (!url) return null;
  if (isVideoUrl(url)) {
    const id = extractVideoId(url);
    return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
  }
  return null;
};

const detectType = (url) => {
  if (!url.trim()) return null;
  if (isVideoUrl(url) && !isPlaylistUrl(url)) return "video";
  if (isPlaylistUrl(url)) return "playlist";
  return "unknown";
};

const typeLabel = {
  video:    { icon: "🎬", text: "Single Video → becomes a 1-video course", color: "#2563eb" },
  playlist: { icon: "📋", text: "Playlist",                                  color: "#7c3aed" },
  unknown:  { icon: "⚠️", text: "Unrecognized URL",                          color: "#ef4444" },
};

// ─── Component ──────────────────────────────────────────────
function SkillModal({ onClose, onSave, existing = null }) {
  const isEdit = !!existing;

  const [tab,       setTab]       = useState("url");
  const [name,      setName]      = useState("");
  const [resources, setResources] = useState([{ url: "", type: null }]);

  const [courses,        setCourses]        = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // ── prefill when editing ───────────────────────────────
  useEffect(() => {
    if (existing) {
      setName(existing.name || "");
      const src = existing.resources || existing.playlists || [];
      setResources(
        src.length
          ? src.map((p) => ({ url: p.url || "", type: p.type || detectType(p.url || "") }))
          : [{ url: "", type: null }]
      );
    }
  }, [existing]);

  // ── fetch incomplete courses when "existing" tab opens ─
  useEffect(() => {
    if (tab !== "existing") return;
    if (courses.length > 0) return;
    const fetch = async () => {
      setCoursesLoading(true);
      try {
        const res = await api.get("/courses/incomplete");
        setCourses(res.data.courses || []);
      } catch (e) {
        console.error("Failed to load courses", e);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetch();
  }, [tab, courses.length]);

  // ── URL tab helpers ────────────────────────────────────
  const addResource = () =>
    setResources((prev) => [...prev, { url: "", type: null }]);

  const updateResource = (index, value) =>
    setResources((prev) =>
      prev.map((r, i) => i === index ? { url: value, type: detectType(value) } : r)
    );

  const removeResource = (index) =>
    setResources((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ url: "", type: null }];
    });

  // ── Save ───────────────────────────────────────────────
  const handleSave = () => {
    if (!name.trim()) return;

    if (tab === "existing" && selectedCourse) {
      // ✅ FIX: use real course URL so getThumb() and resource count work
      // immediately in local state before the backend resolves the courseId
      const courseUrl = selectedCourse.playlistUrl || selectedCourse.url || "";

      onSave({
        type:      "skill",
        name:      name.trim(),
        resources: [{
          url:       courseUrl,
          type:      "existing_course",
          courseId:  selectedCourse._id,
          title:     selectedCourse.title,
          thumbnail: selectedCourse.thumbnail || null,
        }],
        playlists: [{
          url:      courseUrl,
          type:     "existing_course",
          courseId: selectedCourse._id,
        }],
        thumbnail:        selectedCourse.thumbnail || null,
        existingCourseId: selectedCourse._id,
        totalVideos:      selectedCourse.totalVideos || 1,
      });
      return;
    }

    // URL tab
    const filled         = resources.filter((r) => r.url.trim() !== "");
    const firstVideo     = filled.find((r) => r.type === "video");
    const firstPlaylist  = filled.find((r) => r.type === "playlist");

    onSave({
      type:      "skill",
      name:      name.trim(),
      playlists: filled.map((r) => ({ url: r.url, type: r.type })),
      resources: filled.map((r) => ({ url: r.url, type: r.type })),
      thumbnail: getThumbnail(firstVideo?.url || firstPlaylist?.url || ""),
    });
  };

  const videoCount    = resources.filter((r) => r.type === "video").length;
  const playlistCount = resources.filter((r) => r.type === "playlist").length;
  const canSave = name.trim() && (
    tab === "existing"
      ? !!selectedCourse
      : resources.some((r) => r.url.trim())
  );

  // ── Render ─────────────────────────────────────────────
  return (
    <div className="sm-modal">

      {/* Header */}
      <div className="sm-header">
        <div className="sm-header-icon">🎯</div>
        <div>
          <h3>{isEdit ? "Edit Skill" : "Add Skill"}</h3>
          <p className="sm-header-sub">Attach a course, playlist, or single video</p>
        </div>
        <button className="sm-close" onClick={onClose}>✕</button>
      </div>

      {/* Skill name */}
      <div className="sm-field">
        <label className="sm-label">Skill Name</label>
        <input
          className="sm-input"
          placeholder="e.g. React Fundamentals"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      {/* Tab switcher */}
      <div className="sm-tabs">
        <button
          className={`sm-tab ${tab === "url" ? "sm-tab--active" : ""}`}
          onClick={() => setTab("url")}
          type="button"
        >
          🔗 Paste URL
        </button>
        <button
          className={`sm-tab ${tab === "existing" ? "sm-tab--active" : ""}`}
          onClick={() => setTab("existing")}
          type="button"
        >
          📚 My Courses
        </button>
      </div>

      {/* ── URL tab ────────────────────────────────────── */}
      {tab === "url" && (
        <div className="sm-tab-body">

          {(videoCount > 0 || playlistCount > 0) && (
            <div className="sm-summary">
              {playlistCount > 0 && (
                <span className="sm-badge sm-badge--playlist">
                  📋 {playlistCount} playlist{playlistCount > 1 ? "s" : ""}
                </span>
              )}
              {videoCount > 0 && (
                <span className="sm-badge sm-badge--video">
                  🎬 {videoCount} video{videoCount > 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}

          {videoCount > 0 && (
            <div className="sm-tip">
              💡 Each individual video URL will automatically become a 1-video course with full progress tracking.
            </div>
          )}

          <div className="sm-field">
            <label className="sm-label">Resources</label>
            <div className="sm-resources">
              {resources.map((r, index) => {
                const thumb = getThumbnail(r.url);
                const meta  = r.type ? typeLabel[r.type] : null;
                return (
                  <div
                    key={index}
                    className={`sm-resource-row ${r.type ? `sm-resource-row--${r.type}` : ""}`}
                  >
                    <div className="sm-thumb">
                      {thumb
                        ? <img src={thumb} alt="" className="sm-thumb-img" />
                        : <span className="sm-thumb-placeholder">{meta?.icon || "🔗"}</span>
                      }
                    </div>
                    <div className="sm-resource-right">
                      <input
                        className="sm-input sm-resource-input"
                        placeholder="YouTube playlist or video URL"
                        value={r.url}
                        onChange={(e) => updateResource(index, e.target.value)}
                      />
                      {meta && (
                        <span className="sm-type-tag" style={{ color: meta.color }}>
                          {meta.icon} {meta.text}
                        </span>
                      )}
                    </div>
                    <button className="sm-remove-btn" onClick={() => removeResource(index)}>✕</button>
                  </div>
                );
              })}
            </div>
            <button className="sm-add-resource-btn" onClick={addResource}>+ Add URL</button>
          </div>
        </div>
      )}

      {/* ── Existing courses tab ───────────────────────── */}
      {tab === "existing" && (
        <div className="sm-tab-body">
          <p className="sm-existing-hint">
            Pick one of your in-progress courses to attach to this skill.
          </p>

          {coursesLoading && (
            <div className="sm-courses-loading">
              <div className="sm-spinner" />
              <span>Loading your courses…</span>
            </div>
          )}

          {!coursesLoading && courses.length === 0 && (
            <div className="sm-courses-empty">
              No incomplete courses found. Add a course from the dashboard first.
            </div>
          )}

          {!coursesLoading && courses.length > 0 && (
            <div className="sm-courses-list">
              {courses.map((course) => {
                const isSelected = selectedCourse?._id === course._id;
                return (
                  <button
                    key={course._id}
                    className={`sm-course-card ${isSelected ? "sm-course-card--selected" : ""}`}
                    onClick={() => setSelectedCourse(isSelected ? null : course)}
                    type="button"
                  >
                    {course.thumbnail ? (
                      <img src={course.thumbnail} alt="" className="sm-course-thumb" />
                    ) : (
                      <div className="sm-course-thumb-placeholder">📚</div>
                    )}
                    <div className="sm-course-info">
                      <span className="sm-course-title">{course.title}</span>
                      <span className="sm-course-meta">
                        {course.creatorName} · {course.totalVideos} video{course.totalVideos !== 1 ? "s" : ""}
                      </span>
                      <div className="sm-course-progress-bar">
                        <div
                          className="sm-course-progress-fill"
                          style={{ width: `${course.progressPercent || 0}%` }}
                        />
                      </div>
                      <span className="sm-course-progress-text">
                        {course.progressPercent || 0}% complete
                      </span>
                    </div>
                    {isSelected && <span className="sm-course-check">✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="sm-footer">
        <button className="sm-btn sm-btn--cancel" onClick={onClose}>Cancel</button>
        <button
          className="sm-btn sm-btn--save"
          onClick={handleSave}
          disabled={!canSave}
        >
          {isEdit ? "Update Skill" : "Add Skill"}
        </button>
      </div>
    </div>
  );
}

export default SkillModal;